import { h } from '@stencil/core';

import wcShared from '@web-components/shared';

import { InitStore } from '../../../../../../utils/Stores/init-store';
import { Store } from '../../../../../../utils/Stores/store';

interface Service {
  fields?: Record<string, unknown>;
}

function getCustomData() {
  return InitStore?.get('init')?.environment?.custom_data;
}

// Retrieves the list of services as a list
function getServicesName(): string[] {
  return Object.keys(getCustomData()?.public_cloud?.services);
}

// Retrieves the list of regions as a list
function getRegions(): string[] {
  return getCustomData()?.public_cloud?.region;
}

function getSelectedServiceObject(): Service | undefined {
  const selectedServiceName = Store?.get('messageData')?.public_cloud_service;
  const servicesList = getCustomData()?.public_cloud?.services;

  if (!selectedServiceName || !servicesList || !(selectedServiceName in servicesList)) {
    return undefined;
  }

  const service: Service = servicesList[selectedServiceName];
  if (typeof service === 'object' && service !== null) {
    return service;
  }
  return undefined;
}

function hasFieldInService(): boolean {
  const service = getSelectedServiceObject();
  return service && 'fields' in service;
}

function getServiceInfoFieldValue(): string | undefined {
  const service = getSelectedServiceObject();

  if (
    service &&
    'fields' in service &&
    typeof service.fields === 'object' &&
    service.fields !== null
  ) {
    const fieldValue = service.fields['0'];

    return typeof fieldValue === 'string' ? fieldValue : undefined;
  }
  return undefined;
}

function resetServiceInfo(): void {
  Store?.set('messageData', {
    ...Store?.get('messageData'),
    public_cloud_service_info: null,
  });
}

export function getPublicCloudFields(
  checkEmptyField: boolean,
  changeData: (event: Event, value: string) => void,
) {
  const regions = getRegions();
  const services = getServicesName();

  function getServicesFields() {
    return (
      hasFieldInService() && (
        <div>
          <input
            required
            class={{
              'no-margin-bottom':
                (Store?.get('messageData')?.public_cloud_service_info === null ||
                  Store?.get('messageData')?.public_cloud_service_info === '') &&
                checkEmptyField,
            }}
            type="text"
            placeholder={getServiceInfoFieldValue()}
            onChange={(event) => changeData(event, 'public_cloud_service_info')}
          />
          <span
            class={{
              'required-field': true,
              hide:
                (Store?.get('messageData')?.public_cloud_service_info !== null &&
                  Store?.get('messageData')?.public_cloud_service_info !== '') ||
                !checkEmptyField,
            }}
          >
            {wcShared?.translate('*Champ obligatoire')}
          </span>
        </div>
      )
    );
  }

  return (
    <div>
      {/* Service concerned */}
      <select
        required
        class={{
          'no-margin-bottom':
            (Store?.get('messageData')?.public_cloud_service === null ||
              Store?.get('messageData')?.public_cloud_service === '') &&
            checkEmptyField,
        }}
        onChange={(event) => {
          changeData(event, 'public_cloud_service');
          resetServiceInfo();
        }}
      >
        <option
          value=""
          selected
          disabled
          hidden
        >
          {wcShared?.translate('Service concerné')}
        </option>
        {services.map((service) => {
          return <option value={service}>{service}</option>;
        })}
      </select>
      <span
        class={{
          'required-field': true,
          hide:
            (Store?.get('messageData')?.public_cloud_service !== null &&
              Store?.get('messageData')?.public_cloud_service !== '') ||
            !checkEmptyField,
        }}
      >
        {wcShared?.translate('*Champ obligatoire')}
      </span>

      {getServicesFields()}

      {/* Select region */}
      <select
        required
        class={{
          'no-margin-bottom':
            (Store?.get('messageData')?.public_cloud_region === null ||
              Store?.get('messageData')?.public_cloud_region === '') &&
            checkEmptyField,
        }}
        onChange={(event) => changeData(event, 'public_cloud_region')}
      >
        <option
          value=""
          selected
          disabled
          hidden
        >
          {wcShared?.translate('Region')}
        </option>
        {regions.map((region) => {
          return <option value={region}>{region}</option>;
        })}
      </select>
      <span
        class={{
          'required-field': true,
          hide:
            (Store?.get('messageData')?.public_cloud_region !== null &&
              Store?.get('messageData')?.public_cloud_region !== '') ||
            !checkEmptyField,
        }}
      >
        {wcShared?.translate('*Champ obligatoire')}
      </span>
      {/* Select product list */}
      <select
        class={{
          'no-margin-bottom':
            (Store?.get('messageData')?.public_cloud_product === null ||
              Store?.get('messageData')?.public_cloud_product === '') &&
            checkEmptyField,
        }}
        onChange={(event) => changeData(event, 'public_cloud_product')}
      >
        <option
          value=""
          selected
          disabled
          hidden
        >
          {wcShared?.translate('Produit concerne')}
        </option>
        {InitStore?.get('init')?.environment?.public_cloud?.map(
          (product: IWCPublicCloudEnvironment) => {
            return <option value={product.name}>{product.name}</option>;
          },
        )}
      </select>
      <span
        class={{
          'required-field': true,
          hide:
            (Store?.get('messageData')?.public_cloud_product !== null &&
              Store?.get('messageData')?.public_cloud_product !== '') ||
            !checkEmptyField,
        }}
      >
        {wcShared?.translate('*Champ obligatoire')}
      </span>

      {/* Select project list */}
      {Store?.get('messageData')?.public_cloud_product !== null &&
        InitStore?.get('init')?.environment?.public_cloud.find(
          (product: IWCPublicCloudEnvironment) =>
            product.name === Store?.get('messageData')?.public_cloud_product,
        )?.projects?.length > 0 && (
          <div>
            <select
              class={{
                'no-margin-bottom':
                  (Store?.get('messageData')?.public_cloud_project === null ||
                    Store?.get('messageData')?.public_cloud_project === '') &&
                  checkEmptyField,
              }}
              onChange={(event) => changeData(event, 'public_cloud_project')}
            >
              <option
                value=""
                selected={null === Store?.get('messageData')?.public_cloud_project}
                disabled
                hidden
              >
                {wcShared?.translate('Projet concerne')}
              </option>
              {InitStore?.get('init')
                ?.environment?.public_cloud.find(
                  (product: IWCPublicCloudEnvironment) =>
                    product.name === Store?.get('messageData')?.public_cloud_product,
                )
                ?.projects.map((option: IWCPublicCloudProjects) => (
                  <option value={option.open_stack_name}>
                    {option.name} | {option.open_stack_name}
                  </option>
                ))}
            </select>
            <span
              class={{
                'required-field': true,
                hide:
                  (Store?.get('messageData')?.public_cloud_project !== null &&
                    Store?.get('messageData')?.public_cloud_project !== '') ||
                  !checkEmptyField,
              }}
            >
              {wcShared?.translate('*Champ obligatoire')}
            </span>
          </div>
        )}
    </div>
  );
}
